import { askGroq } from "./groqClient";

// ============================================================
// SUGGESTED QUESTIONS
// ============================================================
// Both functions here only ever see the schema (+ small computed
// summaries) — never raw rows — consistent with the rest of the app.
// Failures fall back to an empty list rather than blocking the UI.

function extractQuestions(response, max) {

  const match = response?.match(/\{[\s\S]*\}/);
  if (!match) return [];

  const parsed = JSON.parse(match[0]);
  return (parsed.questions || []).slice(0, max);
}

// Called once right after a dataset is uploaded.
export async function suggestInitialQuestions(schema) {

  if (!schema?.columns?.length) return [];

  const prompt = `
You are suggesting starter questions for a new dataset in InsightPulse AI,
a conversational analytics tool. You only see the schema below, never the
actual rows.

Suggest 5 short, natural questions a business user would actually ask about
THIS dataset — reference real column names/roles from the schema (e.g. the
actual measure and dimension names), not generic filler. Mix simple
questions (top performer, totals) with a trend question (if a time column
exists) and a "why" question (if there's a plausible driver-analysis
angle).

Return ONLY JSON: { "questions": ["...", "...", "...", "...", "..."] }

Dataset Schema:
${JSON.stringify(schema, null, 2)}
`;

  try {

    const response = await askGroq({
      messages: [{ role: "system", content: prompt }],
      temperature: 0.4,
    });

    return extractQuestions(response, 5);

  } catch (err) {
    console.error("suggestInitialQuestions failed:", err);
    return [];
  }
}

// Called after each data-question answer to suggest natural next steps.
export async function suggestFollowUpQuestions(question, computedResultSummary, contextSummary, schema) {

  if (!schema?.columns?.length) return [];

  const prompt = `
You are suggesting follow-up questions after answering a question in
InsightPulse AI. You only see the schema, the conversation context, and the
already-computed result summary below — never raw data rows.

Suggest exactly 3 short, natural follow-up questions that would genuinely
extend THIS specific analysis (e.g. drill into a time period, compare
entities that appeared in the result, ask "why", or break down by another
real dimension from the schema). Do not suggest something unrelated to what
was just discussed. Do not repeat the question that was just asked.

Return ONLY JSON: { "questions": ["...", "...", "..."] }

Question just asked: ${question}

Result summary: ${computedResultSummary || "N/A"}

Conversation context: ${contextSummary}

Dataset Schema:
${JSON.stringify(schema, null, 2)}
`;

  try {

    const response = await askGroq({
      messages: [{ role: "system", content: prompt }],
      temperature: 0.4,
    });

    return extractQuestions(response, 3);

  } catch (err) {
    console.error("suggestFollowUpQuestions failed:", err);
    return [];
  }
}
